<template>
  <NavBar />
  <BackgroundImage
    :imageUrl="require('@/assets/login/bg.png')"
    :customStyle="{ minHeight: '100vh' }"
  >
    <div class="container">
      <a-flex gap="middle" justify="center" vertical style="margin-left:-8px;">
        <a-row :gutter="[0, 24]" justify="center">
          <!-- 顶部品牌与搜索 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="brand-card">
              <div class="brand-header">
                <div class="brand-logo">岐</div>
                <div>
                  <h1 class="brand-title">数慧岐黄 · 专病诊疗中心</h1>
                  <p class="brand-subtitle">以病为纲 · 证病同参 · 联动专药与名医传承</p>
                </div>
              </div>
              <div class="search-section">
                <a-input
                  v-model:value="searchKeyword"
                  size="large"
                  placeholder="搜索专病/证型/症状… 例如：失眠、胃脘痛、月经不调"
                  @pressEnter="handleSearch"
                >
                  <template #prefix>
                    <SearchOutlined />
                  </template>
                </a-input>
                <a-button type="primary" size="large" @click="handleSearch">
                  搜索
                </a-button>
              </div>
              <div class="tag-chips">
                <a-tag
                  v-for="tag in filterTags"
                  :key="tag"
                  :color="activeTags.includes(tag) ? 'blue' : 'default'"
                  class="tag-chip"
                  @click="toggleTag(tag)"
                >
                  {{ tag }}
                </a-tag>
              </div>
            </a-card>
          </a-col>

          <!-- 导航锚点 -->
          <a-col :md="24" :sm="24" :xs="24">
            <div class="nav-links">
              <a-button
                v-for="link in navLinks"
                :key="link.id"
                class="nav-link"
                @click="scrollToSection(link.id)"
              >
                {{ link.label }}
              </a-button>
            </div>
          </a-col>

          <!-- 病情总览 -->
          <a-col :md="24" :sm="24" :xs="24" id="overview">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                病情总览（示例：失眠）
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">基础信息</h3>
                    <p class="info-subtitle">别名：不寐 · ICD-10：F51 · 现代医学归属：睡眠障碍</p>
                    <div class="pill-group">
                      <span v-for="pill in overviewPills" :key="pill" class="pill">{{ pill }}</span>
                    </div>
                    <p class="info-body">
                      中医认识：心神失养、阴阳失交、肝郁化火、痰热内扰等均可致不寐，宜辨体质与病机，调和心肾、疏肝安神。
                    </p>
                    <div class="progress-block">
                      <span class="progress-label">患者教育完成度</span>
                      <a-progress
                        :percent="62"
                        :stroke-width="10"
                        stroke-color="#2D6A4F"
                        :show-info="false"
                      />
                    </div>
                  </div>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">常见诱因</h3>
                    <ul class="info-list">
                      <li v-for="item in overviewTriggers" :key="item">{{ item }}</li>
                    </ul>
                    <div class="pill-group">
                      <span class="pill" v-for="doc in overviewDocs" :key="doc">{{ doc }}</span>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- AI初筛 -->
          <a-col :md="24" :sm="24" :xs="24" id="triage">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                AI初筛与就医分级
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <div class="info-card">
                    <p class="info-subtitle">回答几个问题，获取就医建议（演示数据）</p>
                    <div class="triage-form">
                      <a-select
                        v-model:value="triageForm.duration"
                        placeholder="症状时长"
                        size="large"
                      >
                        <a-select-option value="lt3">＜3个月</a-select-option>
                        <a-select-option value="gte3">≥3个月</a-select-option>
                      </a-select>
                      <a-select
                        v-model:value="triageForm.severe"
                        placeholder="伴严重躯体症状？"
                        size="large"
                      >
                        <a-select-option value="no">否</a-select-option>
                        <a-select-option value="yes">是</a-select-option>
                      </a-select>
                      <a-select
                        v-model:value="triageForm.pregnancy"
                        placeholder="是否妊娠/哺乳"
                        size="large"
                      >
                        <a-select-option value="no">否</a-select-option>
                        <a-select-option value="yes">是</a-select-option>
                      </a-select>
                      <a-button type="primary" size="large" @click="generateTriage">
                        生成建议
                      </a-button>
                    </div>
                    <pre v-if="triageResult" class="triage-output">{{ triageResult }}</pre>
                  </div>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">红旗症状（需尽快就医）</h3>
                    <ul class="info-list">
                      <li v-for="item in triageFlags" :key="item">{{ item }}</li>
                    </ul>
                    <div class="pill-group">
                      <span v-for="marker in triageMarkers" :key="marker" class="tag-pill">{{ marker }}</span>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 证型辨析 -->
          <a-col :md="24" :sm="24" :xs="24" id="pattern">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                证型辨析（结构化四诊数据）
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <a-table
                    :columns="patternColumns"
                    :data-source="patternData"
                    :pagination="false"
                    size="middle"
                    bordered
                    row-key="name"
                  />
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">体质联动</h3>
                    <p class="info-subtitle">推荐与体质结果打通：气郁质、阴虚质、痰湿质、血瘀质等。</p>
                    <div class="pill-group">
                      <span v-for="item in physiqueData" :key="item" class="tag-pill">{{ item }}</span>
                    </div>
                    <div class="action-buttons">
                      <a-button type="primary" @click="syncToMeds">推送到“专药”</a-button>
                      <a-button @click="notify('已保存辨证结果')">保存辨证</a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 诊疗路径 -->
          <a-col :md="24" :sm="24" :xs="24" id="pathway">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                诊疗路径（中西协同）
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :span="24">
                  <div class="pathway-pills">
                    <span v-for="step in pathwaySteps" :key="step" class="pill">{{ step }}</span>
                  </div>
                </a-col>
                <a-col :md="12" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">推荐方药（示例）</h3>
                    <ul class="info-list">
                      <li v-for="item in pathwayHerbs" :key="item">{{ item }}</li>
                    </ul>
                    <a-button type="primary" @click="notify('已跳转至专药联动')">
                      去专药联动
                    </a-button>
                  </div>
                </a-col>
                <a-col :md="12" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">西医常用治疗（摘要）</h3>
                    <p class="info-subtitle">睡眠卫生教育、CBT-I 认知行为治疗；必要时短程药物治疗需遵医嘱。</p>
                    <a-table
                      :columns="westernColumns"
                      :data-source="westernData"
                      :pagination="false"
                      size="small"
                      bordered
                      row-key="type"
                    />
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 专药联动 -->
          <a-col :md="24" :sm="24" :xs="24" id="meds">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                专药联动（智能推荐）
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="item in medsData"
                  :key="item.name"
                  :md="12"
                  :sm="24"
                  :xs="24"
                >
                  <div class="card-block">
                    <h4 class="card-title">{{ item.name }}</h4>
                    <p class="info-subtitle">适配：{{ item.fit }}</p>
                    <div class="pill-group">
                      <span v-for="tag in item.tags" :key="tag" class="tag-pill">{{ tag }}</span>
                    </div>
                    <div class="action-buttons">
                      <a-button type="primary" size="small" @click="notify(`已加入AI调理方案：${item.name}`)">
                        加入方案
                      </a-button>
                      <a-button size="small" @click="notify('跳转到专药页面查看详情')">
                        查看专药详情
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 名医经验 -->
          <a-col :md="24" :sm="24" :xs="24" id="masters">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                名医经验与视频
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="item in masterData"
                  :key="item.doctor"
                  :md="12"
                  :sm="24"
                  :xs="24"
                >
                  <div class="card-block">
                    <h4 class="card-title">{{ item.doctor }} · {{ item.topic }}</h4>
                    <p class="info-subtitle">经验要点：{{ item.tip }}</p>
                    <div class="pill-group">
                      <span class="pill">🎬 讲解视频（占位）</span>
                      <span class="pill">📄 经验方PDF</span>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 病例 -->
          <a-col :md="24" :sm="24" :xs="24" id="cases">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                病例与随访（结构化）
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="item in caseData"
                  :key="item.id"
                  :md="12"
                  :sm="24"
                  :xs="24"
                >
                  <div class="card-block">
                    <h4 class="card-title">病例 {{ item.id }} · {{ item.gender }}{{ item.age }}</h4>
                    <p class="info-subtitle">辨证：{{ item.dx }}</p>
                    <p class="info-subtitle">处方：{{ item.plan }}</p>
                    <p class="info-subtitle">随访：{{ item.follow }}</p>
                    <div class="action-buttons">
                      <a-button size="small" @click="notify('下载病历单（示例）')">
                        下载病历单
                      </a-button>
                      <a-button size="small" @click="notify('继续随访（示例）')">
                        继续随访
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 生活方式 -->
          <a-col :md="24" :sm="24" :xs="24" id="guide">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                生活方式与自我管理
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="12" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">作息建议</h3>
                    <ul class="info-list">
                      <li v-for="item in lifestyleRoutine" :key="item">{{ item }}</li>
                    </ul>
                  </div>
                </a-col>
                <a-col :md="12" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">饮食与茶饮</h3>
                    <ul class="info-list">
                      <li v-for="item in lifestyleDiet" :key="item">{{ item }}</li>
                    </ul>
                  </div>
                </a-col>
              </a-row>
              <p class="footer-note">© 数慧岐黄 · 专病诊疗中心 — 演示页面。建议与处方以专业医师指导为准。</p>
            </a-card>
          </a-col>
        </a-row>
      </a-flex>
    </div>
  </BackgroundImage>
</template>

<script>
import BackgroundImage from "@/components/BackgroundImage.vue";
import NavBar from "@/components/NavBar.vue";
import { SearchOutlined } from "@ant-design/icons-vue";

export default {
  name: "ZhuanbingPage",
  components: {
    BackgroundImage,
    NavBar,
    SearchOutlined,
  },
  data() {
    return {
      searchKeyword: "",
      filterTags: ["内科", "妇科", "儿科", "骨伤", "皮科", "情志"],
      activeTags: [],
      navLinks: [
        { id: "overview", label: "病情总览" },
        { id: "triage", label: "AI初筛" },
        { id: "pattern", label: "证型辨析" },
        { id: "pathway", label: "诊疗路径" },
        { id: "meds", label: "专药联动" },
        { id: "masters", label: "名医经验" },
        { id: "cases", label: "病例随访" },
        { id: "guide", label: "生活方式" },
      ],
      overviewPills: ["发作反复", "入睡困难", "多梦易醒", "日间乏力"],
      overviewTriggers: [
        "情志失调：焦虑、抑郁、过度思虑",
        "作息紊乱：夜间用脑过度、蓝光刺激",
        "饮食不节：辛辣、酒精、夜宵",
        "环境变化：旅行、噪音、温湿度异常",
      ],
      overviewDocs: ["📘 自助手册", "🌙 睡眠记录表"],
      triageForm: {
        duration: "",
        severe: "",
        pregnancy: "",
      },
      triageResult: "",
      triageFlags: [
        "胸闷胸痛、呼吸困难、心悸明显",
        "抑郁伴自伤意念",
        "长期失眠合并高血压、糖尿病控制不佳",
      ],
      triageMarkers: ["分级就诊", "中西协同"],
      patternColumns: [
        { title: "证型", dataIndex: "name", key: "name" },
        { title: "主症", dataIndex: "symptom", key: "symptom" },
        { title: "舌脉", dataIndex: "tongue", key: "tongue" },
        { title: "治法", dataIndex: "therapy", key: "therapy" },
      ],
      patternData: [
        { name: "心肾不交", symptom: "心烦不寐、腰膝酸软", tongue: "舌红少苔 · 脉细数", therapy: "交通心肾、滋阴降火" },
        { name: "肝郁化火", symptom: "烦躁易怒、多梦", tongue: "舌红苔黄 · 脉弦数", therapy: "疏肝泄热、宁心安神" },
        { name: "痰热内扰", symptom: "胸闷痰多、口苦", tongue: "舌红苔黄腻 · 脉滑数", therapy: "清化痰热、和中安神" },
        { name: "气血两虚", symptom: "神疲乏力、心悸健忘", tongue: "舌淡 · 脉细", therapy: "益气补血、养心安神" },
      ],
      physiqueData: ["气郁质", "阴虚质", "痰湿质", "血瘀质", "气虚质"],
      pathwaySteps: [
        "🔎 初诊评估 → 四诊合参 + 量表（PSQI）",
        "🧪 必要检查 → 甲状腺/血糖/维生素D",
        "🌿 中医治疗 → 方药/针灸/耳穴/音疗",
        "💡 生活方式 → CBT-I、作息管理",
        "🔁 随访复评 → 2~4周一次",
      ],
      pathwayHerbs: [
        "酸枣仁汤：养心安神，适用心肝血虚、阴不敛阳",
        "柴胡疏肝散：疏肝理气，情志不舒、胸胁胀痛",
        "黄连温胆汤：痰热内扰、心烦多梦",
      ],
      westernColumns: [
        { title: "类别", dataIndex: "type", key: "type", width: 120 },
        { title: "要点", dataIndex: "detail", key: "detail" },
      ],
      westernData: [
        { type: "非药物", detail: "固定起床时间、减少午睡、睡前光线与屏幕管理" },
        { type: "药物", detail: "短期助眠药遵循医嘱；避免长期依赖" },
      ],
      medsData: [
        { name: "酸枣仁汤", fit: "心肝血虚/阴不敛阳", tags: ["安神", "养血", "助眠"] },
        { name: "柴胡疏肝散", fit: "肝郁化火/情志不舒", tags: ["疏肝", "理气"] },
        { name: "黄连温胆汤", fit: "痰热内扰/心烦多梦", tags: ["清热", "化痰", "和中"] },
        { name: "天王补心丹", fit: "心肾阴虚/虚火内扰", tags: ["滋阴", "清热", "安神"] },
      ],
      masterData: [
        { doctor: "叶天士", topic: "温病安神法", tip: "清心化热，佐以豁痰宁神" },
        { doctor: "张景岳", topic: "补火助阳以济阴", tip: "虚实并见时慎攻伐" },
      ],
      caseData: [
        { id: "A-001", gender: "女", age: 32, dx: "失眠（肝郁化火）", plan: "柴胡疏肝散合酸枣仁汤", follow: "2周改善，4周稳眠" },
        { id: "A-002", gender: "男", age: 45, dx: "失眠（痰热内扰）", plan: "黄连温胆汤加减", follow: "夜醒减少，白日清爽" },
      ],
      lifestyleRoutine: [
        "固定起床时刻，逐步提前入睡时间",
        "晚间远离咖啡因、烟酒；睡前1小时减少屏幕",
        "白天进行有氧+舒缓拉伸",
      ],
      lifestyleDiet: [
        "少辛辣油腻，晚餐留7分饱",
        "安神茶饮：酸枣仁、百合、莲子（体质合适时）",
        "阴虚者：配麦冬/石斛；痰热者：淡竹叶/菊花",
      ],
    };
  },
  methods: {
    handleSearch() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) {
        this.$message.warning("请输入关键词");
        return;
      }
      this.$message.success(`搜索"${keyword}"的结果（演示）`);
    },
    toggleTag(tag) {
      const index = this.activeTags.indexOf(tag);
      if (index > -1) {
        this.activeTags.splice(index, 1);
      } else {
        this.activeTags.push(tag);
      }
      const text = this.activeTags.length ? this.activeTags.join("、") : "全部";
      this.$message.info(`筛选：${text}`);
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    generateTriage() {
      const { duration, severe, pregnancy } = this.triageForm;
      if (!duration || !severe || !pregnancy) {
        this.$message.warning("请完整选择所有筛查项");
        return;
      }
      const level = severe === "yes"
        ? "尽快就医（综合内科/心内或急诊评估）"
        : "一般门诊（中医科/睡眠门诊）";
      const notice = pregnancy === "yes"
        ? "妊娠/哺乳期请遵医嘱，慎用药物。"
        : "遵循医嘱，不滥用助眠药。";
      this.triageResult = `分级建议：${level}\n检查建议：如合并打鼾或肥胖，考虑睡眠监测；情志明显者建议心理评估。\n注意：${notice}`;
    },
    syncToMeds() {
      this.notify('已根据证型生成方药推荐，跳转到“专药联动”');
      this.$nextTick(() => {
        this.scrollToSection('meds');
      });
    },
    notify(message) {
      this.$message.info(message);
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding: 80px 20px 40px;
  min-height: 100vh;
}

.brand-card {
  border-radius: 16px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(16, 65, 181, 0.12);

  :deep(.ant-card-body) {
    padding: 32px;

    @media (max-width: 768px) {
      padding: 24px;
    }
  }
}

.section-card {
  border-radius: 16px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(16, 65, 181, 0.12);

  :deep(.ant-card-body) {
    padding: 32px;

    @media (max-width: 768px) {
      padding: 24px;
    }
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 22px;
  font-weight: 700;
  color: #1041b5;
  margin: 0 0 24px;

  @media (max-width: 768px) {
    font-size: 19px;
  }
}

.title-bar {
  width: 8px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(180deg, #c9a227 0%, #eed36b 100%);
  box-shadow: 0 2px 8px rgba(201, 162, 39, 0.3);
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.brand-logo {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eed36b, #c9a227);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: #2d6a4f;
  box-shadow: 0 4px 16px rgba(16, 65, 181, 0.15);
}

.brand-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #1041b5;
}

.brand-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

.search-section {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.tag-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-chip {
  cursor: pointer;
  border-radius: 999px;
  padding: 6px 14px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(94, 123, 243, 0.25);
  }
}

.nav-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;

  .nav-link {
    border-radius: 999px;
    border: 1px solid rgba(94, 123, 243, 0.2);
    background: rgba(255, 255, 255, 0.9);
    color: #1041b5;
    padding: 4px 12px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, rgba(94, 123, 243, 0.12), rgba(16, 65, 181, 0.1));
      border-color: #5e7bf3;
      transform: translateY(-2px);
    }
  }
}

.info-card {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid rgba(94, 123, 243, 0.15);
  padding: 24px;
  box-shadow: 0 6px 24px rgba(16, 65, 181, 0.08);
  height: 100%;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(16, 65, 181, 0.16);
    border-color: rgba(94, 123, 243, 0.35);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
}

.info-title {
  font-size: 18px;
  font-weight: 700;
  color: #1041b5;
  margin: 0 0 12px;
}

.info-subtitle {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 12px;
}

.info-body {
  font-size: 15px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 18px;
}

.info-list {
  padding-left: 18px;
  margin: 0 0 16px;
  color: #445069;
  font-size: 14px;
  line-height: 1.7;
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(94, 123, 243, 0.08), rgba(16, 65, 181, 0.05));
  border: 1px dashed rgba(201, 162, 39, 0.6);
  color: #2d6a4f;
  font-size: 13px;
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(94, 123, 243, 0.08);
  color: #1041b5;
  font-size: 12px;
  border: 1px solid rgba(94, 123, 243, 0.2);
}

.progress-block {
  margin-top: 12px;

  .progress-label {
    display: block;
    font-size: 13px;
    color: #64748b;
    margin-bottom: 6px;
  }
}

.triage-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  :deep(.ant-select) {
    flex: 1;
    width: 100%;
    min-width: 220px;
  }

  :deep(.ant-select-selector) {
    width: 100% !important;
  }
}

.triage-output {
  background: rgba(94, 123, 243, 0.08);
  border: 1px solid rgba(94, 123, 243, 0.2);
  border-radius: 12px;
  padding: 16px;
  font-size: 13px;
  color: #2d3436;
  white-space: pre-wrap;
  margin: 0;
}

.pathway-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.card-block {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid rgba(94, 123, 243, 0.15);
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 6px 24px rgba(16, 65, 181, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 32px rgba(16, 65, 181, 0.18);
    border-color: rgba(94, 123, 243, 0.35);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: #1041b5;
  margin: 0;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.footer-note {
  margin-top: 24px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
}

:deep(.ant-table) {
  font-size: 13px;

  .ant-table-thead > tr > th {
    background: rgba(94, 123, 243, 0.1);
    color: #1041b5;
    font-weight: 600;
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(94, 123, 243, 0.05);
  }
}
</style>
